import type { PerfilUsuario } from '@/features/auth/tipos';

export interface Ubs {
  id: string;
  nome: string;
  /** Reguladores responsáveis; lista vazia: ninguém regula a unidade. */
  reguladores: { id: string; nome: string; email: string }[];
  cnes: string | null;
  email: string;
  telefone: string | null;
  endereco: string | null;
  ativa: boolean;
  criadoEm: string;
  _count?: { usuarios: number; solicitacoes: number };
}

export interface UbsResumida {
  id: string;
  nome: string;
}

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  perfil: PerfilUsuario;
  ativo: boolean;
  ubsId: string | null;
  ubs: UbsResumida | null;
  ultimoLoginEm: string | null;
  precisaTrocarSenha: boolean;
  criadoEm: string;
  /** Unidades sob gestão — preenchido apenas no perfil Gestor. */
  ubsGeridas?: { ubs: UbsResumida }[];
}

export interface Especialidade {
  id: string;
  nome: string;
  codigo: string | null;
  descricao: string | null;
  ativa: boolean;
  _count?: { solicitacoes: number };
}

export const TIPOS_SOLICITACAO = ['CONSULTA', 'EXAME', 'PROCEDIMENTO'] as const;
export type TipoSolicitacao = (typeof TIPOS_SOLICITACAO)[number];

export const ROTULOS_TIPO: Record<TipoSolicitacao, string> = {
  CONSULTA: 'Consulta',
  EXAME: 'Exame',
  PROCEDIMENTO: 'Procedimento',
};

export interface Procedimento {
  id: string;
  nome: string;
  codigo: string | null;
  tipo: TipoSolicitacao;
  ativo: boolean;
  /** Preenchidos pela importação da Tabela Unificada; nulos nos manuais. */
  competenciaSigtap: string | null;
  importadoEm: string | null;
}

// --- Importação da Tabela Unificada (SIGTAP) -------------------------------

export type SituacaoItemSigtap = 'NOVO' | 'ATUALIZAR' | 'SEM_MUDANCA';

export interface TabelaSigtapDisponivel {
  arquivo: string;
  competencia: string;
  versao: string | null;
  tamanhoBytes: number;
}

export interface ItemPreviaSigtap {
  codigo: string;
  nome: string;
  tipo: TipoSolicitacao;
  grupo: string;
  grupoNome: string;
  subgrupoNome: string;
  /** Grupo que o KAVI sugere importar: diagnóstica e clínicos. */
  sugerido: boolean;
  situacao: SituacaoItemSigtap;
  procedimentoId: string | null;
  mudancas: string[];
}

export interface PreviaSigtap {
  competencia: string;
  origem: 'FTP' | 'ARQUIVO';
  arquivo: string | null;
  itens: ItemPreviaSigtap[];
  grupos: { codigo: string; nome: string; total: number; sugerido: boolean }[];
  resumo: {
    total: number;
    novos: number;
    atualizar: number;
    semMudanca: number;
    sugeridos: number;
  };
}

export interface ResultadoImportacaoSigtap {
  criados: number;
  atualizados: number;
  inalterados: number;
  ignorados: number;
  /** Nome repetido no SIGTAP: o KAVI só aceita um por nome e tipo. */
  duplicados: { codigo: string; nome: string }[];
  competencia: string;
}

/** Local de atendimento oferecido no agendamento manual da Regulação. */
export interface LocalAtendimento {
  id: string;
  nome: string;
  cnes: string | null;
  endereco: string | null;
  telefone: string | null;
  ativo: boolean;
}

/** Profissional oferecido no agendamento manual da Regulação. */
export interface Profissional {
  id: string;
  nome: string;
  conselho: string | null;
  ativo: boolean;
  especialidadeId: string | null;
  especialidade: { id: string; nome: string } | null;
  /** Preenchidos pela importação do CNES; nulos nos cadastrados à mão. */
  cns: string | null;
  cbo: string | null;
  cboDescricao: string | null;
  cnesOrigem: string | null;
  importadoEm: string | null;
}

// --- Importação do quadro de profissionais pelo CNES -----------------------

export type SituacaoItemCnes = 'NOVO' | 'ATUALIZAR' | 'SEM_MUDANCA';

export interface ItemPreviaCnes {
  cns: string;
  nome: string;
  cbo: string;
  cboDescricao: string;
  outrosCbos: string[];
  vinculo: string | null;
  familia: string | null;
  /** Ocupação que atende paciente — decide o que vem pré-marcado. */
  assistencial: boolean;
  situacao: SituacaoItemCnes;
  profissionalId: string | null;
  mudancas: string[];
}

export interface PreviaCnes {
  localId: string;
  localNome: string;
  cnes: string;
  /** Nome do estabelecimento no CNES: confirma que o código é o esperado. */
  nomeFantasiaCnes: string | null;
  competencia: string | null;
  itens: ItemPreviaCnes[];
  ausentes: { id: string; nome: string; cns: string | null }[];
  resumo: { novos: number; atualizar: number; semMudanca: number; ausentes: number };
}

export interface ResultadoImportacaoCnes {
  criados: number;
  atualizados: number;
  inalterados: number;
  inativados: number;
  ignorados: number;
}

// Projeções enxutas devolvidas pelos endpoints `/ativos`, que alimentam os
// menus de escolha do agendamento manual.

export type LocalAtivo = Pick<LocalAtendimento, 'id' | 'nome' | 'endereco' | 'telefone'>;

export type ProcedimentoAtivo = Pick<Procedimento, 'id' | 'nome' | 'tipo'>;

export interface ProfissionalAtivo {
  id: string;
  nome: string;
  conselho: string | null;
  especialidade: { id: string; nome: string } | null;
}

export interface RegistroAuditoria {
  id: string;
  acao: string;
  entidade: string;
  entidadeId: string | null;
  usuarioId: string | null;
  usuario: { id: string; nome: string; email: string; perfil: PerfilUsuario } | null;
  ip: string | null;
  userAgent: string | null;
  detalhes: Record<string, unknown> | null;
  criadoEm: string;
}
